import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, stats } from '@/mocks/testimonials';

const milestones = [
  { year: '2014', title: '平台创立', desc: '91视频网站正式上线，开始为用户提供视频内容导航服务' },
  { year: '2016', title: '用户突破百万', desc: '日活跃用户达到100万，成为国内领先的视频导航平台之一' },
  { year: '2018', title: '全面升级', desc: '完成平台技术架构升级，引入智能推荐算法，大幅提升用户体验' },
  { year: '2020', title: '内容生态完善', desc: '覆盖电影、剧集、综艺、动漫、纪录片、音乐六大核心分类' },
  { year: '2022', title: '移动端优化', desc: '全面适配移动端访问，推出响应式设计，随时随地畅享视频内容' },
  { year: '2024', title: '品牌焕新', desc: '完成品牌视觉升级，以全新的面貌为用户带来更优质的服务' },
];

const faqs = [
  { q: '91视频网站提供哪些类型的视频内容？', a: '我们提供六大核心分类的视频内容导航：电影大片、电视剧集、综艺节目、动漫动画、纪录片和音乐MV。每个分类下都有丰富的精选内容供您选择。' },
  { q: '91视频网站是免费的吗？', a: '91视频网站的内容导航服务完全免费。我们致力于为用户提供便捷的视频发现体验，帮助您快速找到感兴趣的内容。' },
  { q: '如何提交视频内容或合作申请？', a: '如果您有视频内容想要推荐或希望与我们进行商务合作，可以通过页面底部的联系方式与我们取得联系，我们的团队会在3个工作日内回复。' },
  { q: '91视频网站的视频内容来源是什么？', a: '我们整合了全网优质的视频平台资源，为用户提供统一的导航入口。所有内容均链接至正规授权的视频平台。' },
  { q: '如何使用搜索功能快速找到想看的视频？', a: '点击导航栏的搜索图标，输入视频名称、演员或关键词，我们的智能搜索系统会为您快速匹配相关结果。' },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 91视频网站 | 品牌介绍与联系方式';
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems = [{ label: '关于我们' }];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 91视频网站',
    description: '了解91视频网站的品牌故事、发展历程和联系方式。',
    url: 'http://ewqivah.cn/about',
    mainEntity: {
      '@type': 'Organization',
      name: '91视频网站',
      url: 'http://ewqivah.cn/',
      logo: 'http://ewqivah.cn/logo.png',
      description: '领先的在线视频内容导航平台',
      foundingDate: '2014',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CN',
        addressLocality: '北京市',
        addressRegion: '朝阳区',
        streetAddress: '望京SOHO',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-9191',
        contactType: '客户服务',
        availableLanguage: 'Chinese',
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };

  return (
    <main>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 px-4 md:px-6 lg:px-8 bg-foreground-950">
        <div className="w-full max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />
          <h1 className="text-3xl md:text-5xl font-black text-white mt-4 mb-3">关于我们</h1>
          <p className="text-foreground-400 text-base md:text-lg max-w-xl">
            了解91视频网站的品牌故事、发展历程和联系方式
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center mb-16">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full mb-4">
                品牌故事
              </span>
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-5">
                让好内容
                <br />
                <span className="text-primary-500">触手可及</span>
              </h2>
              <p className="text-foreground-500 text-base md:text-lg leading-relaxed mb-5">
                91视频网站创立于2014年，从最初的简单视频聚合平台，发展成为如今覆盖六大核心分类、汇聚五万余部优质视频内容的综合性视频导航网站。十年来，我们始终坚守"让好内容触手可及"的初心。
              </p>
              <p className="text-foreground-500 text-base md:text-lg leading-relaxed mb-5">
                我们深知在海量的互联网内容中发现优质视频的困难。因此，91视频网站致力于通过专业的内容筛选、智能的推荐算法和清晰的分类体系，帮助每一位用户节省寻找内容的时间，将更多精力投入到享受精彩视频之中。
              </p>
              <p className="text-foreground-500 text-base md:text-lg leading-relaxed">
                未来，我们将继续深耕视频内容领域，不断完善平台功能，提升用户体验，努力成为中国用户最信赖的视频内容发现平台。
              </p>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Diverse%20team%20of%20young%20professionals%20collaborating%20in%20modern%20bright%20office%20space%20with%20large%20screens%20showing%20video%20content%2C%20warm%20natural%20lighting%2C%20contemporary%20workspace%20design%20with%20plants%20and%20comfortable%20seating%2C%20editorial%20photography%20with%20soft%20neutral%20warm%20tones&width=800&height=600&seq=about-team&orientation=landscape"
                alt="91视频网站团队"
                className="w-full rounded-2xl object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-background-100 rounded-xl p-5 md:p-6 text-center border border-background-200/70">
                <p className="text-2xl md:text-3xl font-black text-primary-600 mb-1">{stat.value}</p>
                <p className="text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full mb-4">
              发展历程
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950">我们的成长足迹</h2>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-1/2" />
            <div className="space-y-8 md:space-y-12">
              {milestones.map((m, i) => (
                <div key={m.year} className={`relative flex items-start gap-6 md:gap-0 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`hidden md:block w-1/2 ${i % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                    <div className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70">
                      <span className="text-primary-600 font-black text-lg">{m.year}</span>
                      <h3 className="text-foreground-900 font-bold text-base mt-1 mb-2">{m.title}</h3>
                      <p className="text-foreground-500 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>

                  <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full border-4 border-background-100 md:-translate-x-1/2 mt-2 z-10" />

                  <div className="md:hidden flex-1 pl-10">
                    <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                      <span className="text-primary-600 font-black text-lg">{m.year}</span>
                      <h3 className="text-foreground-900 font-bold text-base mt-1 mb-2">{m.title}</h3>
                      <p className="text-foreground-500 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>

                  <div className="hidden md:block w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full mb-4">
              用户评价
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950">口碑与声誉</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-background-100 rounded-2xl p-6 border border-background-200/70">
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className={`ri-star-fill text-sm ${i < t.rating ? 'text-amber-400' : 'text-background-300'}`} />
                  ))}
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-5">{t.content}</p>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                  <div>
                    <p className="text-foreground-900 font-semibold text-sm">{t.name}</p>
                    <p className="text-foreground-500 text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="w-full max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full mb-4">
              常见问题
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950">常见问题解答</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <span className="text-foreground-900 font-semibold text-sm md:text-base pr-4">{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform shrink-0" />
                </summary>
                <div className="px-5 pb-5 text-foreground-500 text-sm leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full mb-4">
              联系我们
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950">联系方式</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-10">
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 text-center border border-background-200/70">
              <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-primary-600" />
              </div>
              <h3 className="text-foreground-900 font-bold text-base mb-2">电子邮箱</h3>
              <p className="text-foreground-500 text-sm">hello@ewqivah.cn</p>
              <p className="text-foreground-500 text-sm">hello@ewqivah.cn</p>
            </div>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 text-center border border-background-200/70">
              <div className="w-14 h-14 bg-accent-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-accent-600" />
              </div>
              <h3 className="text-foreground-900 font-bold text-base mb-2">客服热线</h3>
              <p className="text-foreground-500 text-sm">400-888-9191</p>
              <p className="text-foreground-500 text-sm">服务时间 9:00-21:00</p>
            </div>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 text-center border border-background-200/70">
              <div className="w-14 h-14 bg-secondary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-2xl text-secondary-600" />
              </div>
              <h3 className="text-foreground-900 font-bold text-base mb-2">公司地址</h3>
              <p className="text-foreground-500 text-sm">北京市朝阳区望京SOHO</p>
              <p className="text-foreground-500 text-sm">T3座 28层</p>
            </div>
          </div>

          <div className="bg-foreground-950 rounded-2xl p-6 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-white text-xl md:text-2xl font-bold mb-3">订阅我们的资讯</h3>
                <p className="text-foreground-400 text-sm md:text-base leading-relaxed">
                  订阅91视频网站资讯邮件，第一时间获取最新视频推荐、行业动态和独家内容。
                </p>
              </div>
              <form
                action="#newsletter-form"
                method="post"
                id="newsletter-form"
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="请输入您的邮箱地址"
                  required
                  className="flex-1 px-4 py-3 bg-foreground-900 border border-foreground-800 rounded-xl text-white text-sm placeholder:text-foreground-600 outline-none focus:border-primary-500 transition-colors"
                />
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary-500 text-white font-medium rounded-xl hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                >
                  立即订阅
                  <i className="ri-arrow-right-line" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
